import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Popover } from '../ui/Popover';
import { Chip, ToggleChips } from '../ui/bits';
import { BookCell } from '../ui/ListTable';
import { MenuList, PillMenu, type MenuChoice } from '../ui/PillMenu';
import { Sheet } from '../book/reading';
import { INTEREST_HINTS, NOT_FOR_ME_CHIPS, interestHint, moveChoices } from '../book/changes';
import { moveBook, moveBooks, setBooks } from './actions';
import type { Status } from '../book/types';
import { useNotice } from '../shell/notice';
import { useWide } from '../shell/hooks';
import type { BookSheetKind } from './BookSheet';
import { num, selectedText, shared, shelfName, type ListTab, type QueueRow } from './model';

// Pieces the Queue's tabs share (Part 1's list and Part 2's Revisit): Interest and read type as
// value pills, the one row Move ▾, the bulk bar's menus, and the sheets for several books at once.

export const NARROW_INTEREST = '(max-width: 1000px)';

/**
 * The book cell of a Queue-style table (R6 T5): the shared BookCell (cover, the title in the serif
 * over the author), and, where the columns fold (up to 1,000 px, and on a phone), the folded
 * columns as a grey line under the author (`.q-meta`).
 */
export function RowBook({ title, sub, cover, meta, onOpen }: { title: string; sub: ComponentChildren; cover?: string | null; meta?: ComponentChildren; onOpen: () => void }) {
  return (
    <BookCell
      title={title}
      cover={cover ?? null}
      onOpen={onOpen}
      author={
        <>
          <span class="q-sub">{sub}</span>
          {meta ? <span class="q-sub q-meta">{meta}</span> : null}
        </>
      }
    />
  );
}

/** Select several's ticks: one per row, and one in the header for every book showing. */
export function RowCheck({ title, on, onToggle }: { title: string; on: boolean; onToggle: () => void }) {
  return (
    <span class="q-check">
      <input type="checkbox" aria-label={`Select ${title}`} checked={on} onChange={onToggle} />
    </span>
  );
}
export function SelectShown({ shownIds, selected, setSelected }: { shownIds: string[]; selected: string[]; setSelected: (ids: string[]) => void }) {
  const all = shownIds.length > 0 && shownIds.every((id) => selected.includes(id));
  return (
    <span class="q-check">
      <input
        type="checkbox"
        aria-label="Select every book showing"
        checked={all}
        onChange={() => setSelected(all ? selected.filter((id) => !shownIds.includes(id)) : [...new Set([...selected, ...shownIds])])}
      />
    </span>
  );
}

/** Two clicks on one header: ▲ then ▼ (R6 T4); another header starts at ▲. */
export const flipSort = <K extends string>(sort: { key: K; reversed: boolean }, k: K) => (sort.key === k ? { key: k, reversed: !sort.reversed } : { key: k, reversed: false });

const INTEREST_CHOICES: MenuChoice<number | null>[] = [
  ...[5, 4, 3, 2, 1].map((n) => ({
    value: n as number | null,
    label: (
      <>
        <span class="q-int-dots" aria-hidden="true">
          {'●'.repeat(n)}
          {'○'.repeat(5 - n)}
        </span>{' '}
        {interestHint(n)}
      </>
    ),
  })),
  { value: null, label: 'Interest not set', divided: true },
];

/**
 * Interest as a value pill (R4, Kristi Nov 1): "● 4 ▾", or the dashed "Interest ▾" when not set;
 * the five levels and Interest not set, ✓ on the current one.
 */
export function InterestMenu({ row, onPick }: { row: Pick<QueueRow, 'interest' | 'title'>; onPick: (v: number | null) => void }) {
  const now = row.interest ?? null;
  return (
    <PillMenu<number | null>
      label={
        now ? (
          <>
            <span class="q-int-dot" aria-hidden="true">
              ●
            </span>
            {now}
          </>
        ) : (
          'Interest'
        )
      }
      ariaLabel={`Interest of ${row.title}: ${now ? interestHint(now) : 'not set'}`}
      sheetTitle={`Interest of ${row.title}`}
      title={now ? interestHint(now) : 'Interest not set'}
      empty={!now}
      class={now ? 'value q-int-pill' : 'q-int-pill'}
      choices={INTEREST_CHOICES}
      current={now}
      onPick={(v) => v !== now && onPick(v)}
    />
  );
}

export type BulkItem = { label: string; run: () => void; current?: boolean; divided?: boolean } | { heading: string };

/** A bulk bar menu: "Move ▾", "Read type ▾", "Interest ▾"; ✓ where every selected book shares the value. */
export function BulkMenu({ label, items, note }: { label: string; items: BulkItem[]; note?: string }) {
  const choices: MenuChoice<number>[] = [];
  const runs: (() => void)[] = [];
  let heading: string | undefined;
  let current: number | null = null;
  for (const it of items) {
    if ('heading' in it) {
      heading = it.heading;
      continue;
    }
    if (it.current) current = runs.length;
    choices.push({ value: runs.length, label: it.label, heading, divided: it.divided });
    runs.push(it.run);
    heading = undefined;
  }
  return <PillMenu<number> label={label} ariaLabel={label} sheetTitle={label} choices={choices} current={current} onPick={(i) => runs[i]?.()} extra={note ? <p class="menu-note">{note}</p> : undefined} />;
}

/** Not for me… from the Queue: the book page's reasons and your words; the kind is worked out per book. */
export function NotForMeSheet({ title, many, onClose, onSave }: { title: string; many: boolean; onClose: () => void; onSave: (chips: string[], reason: string) => Promise<void> }) {
  const [chips, setChips] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Sheet
      title={`Move ${title} to Not for me?`}
      sub={many ? 'Each counts as Passed on, or Abandoned if you stopped reading it. You can bring them back any time.' : 'It counts as Passed on, or Abandoned if you stopped reading it. You can bring it back any time.'}
      onClose={onClose}
    >
      <ToggleChips class="bk-dialog-chips" label="Reasons" choices={NOT_FOR_ME_CHIPS} on={chips} onChange={setChips} small={false} />
      <label class="bk-field">
        <span class="bk-label">Why, in your words</span>
        <input type="text" value={reason} maxLength={2000} placeholder="Optional · only you see this" onInput={(e) => setReason((e.target as HTMLInputElement).value)} />
      </label>
      <div class="bk-buttons">
        <button
          type="button"
          class="btn-main"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await onSave(chips, reason.trim());
            setBusy(false);
          }}
        >
          Move to Not for me
        </button>
        <button type="button" class="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  );
}

/**
 * Several books to Up Next or Future reads: where on the shelf, as for one book (Kristi, Oct 29).
 * Their old places (or the bottom) · Top · Bottom; they keep the list's order. No # for several.
 */
export function BulkPlaceSheet({ to, count, onClose, onMove }: { to: ListTab; count: number; onClose: () => void; onMove: (place: 'kept' | 'top' | 'bottom') => void }) {
  const [place, setPlace] = useState<'kept' | 'top' | 'bottom'>('kept');
  const options: ['kept' | 'top' | 'bottom', string][] = [
    ['kept', 'Their old places, or the bottom'],
    ['top', 'Top'],
    ['bottom', 'Bottom'],
  ];
  return (
    <Sheet title={`Move ${count === 1 ? '1 book' : `${num(count)} books`} to ${shelfName(to)}`} sub="They keep their sequence in this list." onClose={onClose}>
      <fieldset class="bk-place">
        <legend class="bk-label">Where on {shelfName(to)}</legend>
        <div class="bk-dialog-chips">
          {options.map(([k, label]) => (
            <Chip key={k} label={label} on={place === k} onToggle={() => setPlace(k)} />
          ))}
        </div>
      </fieldset>
      <div class="bk-buttons">
        <button type="button" class="btn-main" onClick={() => onMove(place)}>
          Move
        </button>
        <button type="button" class="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  );
}

/** Read type as a value pill (R4): "▌Absorbing ▾", or the dashed "No read type ▾"; ✓ on the current one. */
export function TypeMenu({ row, names, onPick }: { row: Pick<QueueRow, 'title' | 'readType' | 'readTypeName'>; names: string[]; onPick: (n: string | null) => void }) {
  const now = row.readTypeName ?? null;
  const choices: MenuChoice<string | null>[] = [...names.map((n) => ({ value: n as string | null, label: n })), ...(now ? [{ value: null, label: 'No read type', divided: true }] : [])];
  return (
    <PillMenu<string | null>
      label={now ?? 'No read type'}
      ariaLabel={`Read type of ${row.title}: ${now ?? 'no read type'}`}
      sheetTitle={`Read type of ${row.title}`}
      empty={!now}
      bar={now && row.readType ? `var(--rt-${row.readType})` : undefined}
      class={now ? 'value' : ''}
      choices={choices}
      current={now}
      onPick={(n) => n !== now && onPick(n)}
    />
  );
}

/** A shelf's own places in a row's Move ▾ (Up Next and Future reads): To the top, To the bottom, Move to #…. */
export interface Within {
  shelf: string;
  rank: number;
  max: number;
  /** Sorted another way: say a new place counts in your order. */
  reorder: boolean;
  onTop: () => void;
  onBottom: () => void;
  onNumber: (n: number) => void;
}

type MoveValue = number | 'top' | 'bottom' | 'number' | 'extra';

/**
 * The one Move ▾ for a row (Queue, Revisit, Recently added, Review): the book page's moves, ✓ on
 * the shelf it's on, each opening its sheet; on Up Next and Future reads, the shelf's own places
 * too. On a phone the trigger is ⋯ (Kristi, Oct 29; every row list, Nov 1). A choice that can't be
 * made here (Finished… while edit mode is off) isn't offered.
 */
export function MoveMenu({
  row,
  pastRead,
  onSheet,
  extra,
  within,
  asValue,
}: {
  row: { title: string; status: Status | null };
  pastRead: boolean;
  onSheet: (what: BookSheetKind) => void;
  extra?: { label: string; note?: string; run: () => void };
  within?: Within;
  /**
   * Drawn as the row's Shelf value instead of "Move ▾" (filters review, Kristi Nov 2): a book with
   * no shelf yet shows a dashed "No shelf yet ▾" that opens the same Move to, at every width.
   */
  asValue?: string;
}) {
  const phone = !useWide();
  const [open, setOpen] = useState(false);
  const [numbering, setNumbering] = useState(false);
  const close = () => {
    setOpen(false);
    setNumbering(false);
  };
  // Books on these lists aren't being read (a book being read is on Currently Reading), so no open read.
  const moves = moveChoices({ status: row.status, reads: [] }, { pastRead }).filter((c) => c.here || c.to || c.opens);
  const choices: MenuChoice<MoveValue>[] = moves.map((c, i) => ({ value: i, label: c.label, note: c.here ? undefined : c.note || undefined, heading: i === 0 ? 'Move to' : undefined }));
  if (extra) choices.push({ value: 'extra', label: extra.label, note: extra.note, divided: true });
  if (within) {
    choices.push({ value: 'top', label: '⤒ To the top', heading: `Within ${within.shelf}` }, { value: 'bottom', label: '⤓ To the bottom' }, { value: 'number', label: 'Move to #…' });
  }
  const here = moves.findIndex((c) => c.here);
  const pick = (v: MoveValue) => {
    if (v === 'number') return setNumbering(true);
    close();
    if (v === 'extra') return extra?.run();
    if (v === 'top') return within?.onTop();
    if (v === 'bottom') return within?.onBottom();
    const c = moves[v];
    if (!c || c.here) return;
    if (c.opens === 'start') onSheet({ kind: 'start' });
    else if (c.opens === 'past') onSheet({ kind: 'past' });
    else if (c.to === 'not_for_me') onSheet({ kind: 'not_for_me' });
    else if (c.to === 'up_next' || c.to === 'future_reads') onSheet({ kind: 'place', to: c.to });
    else if (c.to === 'reference') onSheet({ kind: 'reference' });
  };
  return (
    <span class="pop-host">
      <button
        type="button"
        class={asValue ? `btn-secondary pill-menu${row.status === null ? ' empty' : ''}` : phone ? 'round-btn q-more' : 'btn-secondary pill-menu'}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={asValue ? `Shelf for ${row.title}: ${asValue}` : `Move ${row.title}`}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {asValue ? (
          <>
            {asValue} <span aria-hidden="true">▾</span>
          </>
        ) : phone ? (
          '⋯'
        ) : (
          <>
            Move <span aria-hidden="true">▾</span>
          </>
        )}
      </button>
      <Popover open={open} onClose={close} label={`Move ${row.title}`} sheetTitle={`Move ${row.title}`}>
        {numbering && within ? (
          <MoveToNumber
            title={row.title}
            rank={within.rank}
            max={within.max}
            onMove={(n) => {
              close();
              within.onNumber(n);
            }}
            onTop={() => {
              close();
              within.onTop();
            }}
            onBottom={() => {
              close();
              within.onBottom();
            }}
            onCancel={close}
          />
        ) : (
          <MenuList<MoveValue>
            choices={choices}
            current={here >= 0 ? here : null}
            onPick={pick}
            extra={within && !within.reorder ? <p class="menu-note">Sorted another way: a new place counts in your reading order.</p> : undefined}
          />
        )}
      </Popover>
    </span>
  );
}

/** Move to #…: a number, or To the top / To the bottom. */
export function MoveToNumber({ rank, max, onMove, onTop, onBottom, onCancel }: { title: string; rank: number; max: number; onMove: (n: number) => void; onTop: () => void; onBottom: () => void; onCancel: () => void }) {
  const [n, setN] = useState('');
  const [err, setErr] = useState('');
  const go = () => {
    const v = Number(n);
    if (!/^\d+$/.test(n.trim()) || v < 1) return setErr(`A number from 1 to ${num(max)}`);
    onMove(Math.min(v, max));
  };
  return (
    <div class="q-moveto">
      <p class="q-moveto-now">
        Now #{num(rank)} of {num(max)}
      </p>
      <label class="q-moveto-field">
        <span>Move to #</span>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={max}
          value={n}
          onInput={(e) => {
            setN((e.target as HTMLInputElement).value);
            setErr('');
          }}
          onKeyDown={(e) => e.key === 'Enter' && go()}
        />
        <button type="button" class="btn-main" onClick={go}>
          Move
        </button>
      </label>
      {err ? <p class="q-err">{err}</p> : null}
      <div class="q-moveto-ends">
        <button type="button" class="btn-secondary" onClick={onTop}>
          ⤒ To the top
        </button>
        <button type="button" class="btn-secondary" onClick={onBottom}>
          ⤓ To the bottom
        </button>
        <button type="button" class="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** A selected row, as the bulk bar's menus need it (✓ where every selected book shares a value). */
export type BulkRow = { id: string; title: string; status?: Status | null; readTypeName?: string | null; interest?: number | null; formats?: string[] };

type BulkSet = (field: 'interest' | 'read_type', value: number | string | null, what: string) => void;

/** Read type ▾ in a bulk bar: your read types, then No read type; ✓ on the one every selected book has. */
export function readTypeItems(readTypes: string[], picked: BulkRow[], set: BulkSet): BulkItem[] {
  const now = shared(picked, (r) => r.readTypeName ?? null);
  return [
    ...readTypes.map((n) => ({ label: n, run: () => set('read_type', n, `set to ${n}`), current: now === n })),
    { label: 'No read type', run: () => set('read_type', null, 'read type cleared'), current: now === null, divided: true },
  ];
}

/** Interest ▾ in a bulk bar: the five levels, then Interest not set; ✓ on the one every selected book has. */
export function interestItems(picked: BulkRow[], set: BulkSet): BulkItem[] {
  const now = shared(picked, (r) => r.interest ?? null);
  return [
    ...[5, 4, 3, 2, 1].map((n) => ({ label: `${'●'.repeat(n)}${'○'.repeat(5 - n)} ${INTEREST_HINTS[n - 1]}`, run: () => set('interest', n, `set to Interest ${n}`), current: now === n })),
    { label: 'Interest not set', run: () => set('interest', null, 'Interest cleared'), current: now === null, divided: true },
  ];
}

/**
 * The bulk bar for the tabs without a shelf order (Revisit, Recently added): Move (Up Next…,
 * Future reads…, Not for me…, Reference), Read type and Interest for the selected books, one Undo
 * each, in the list's order. Up Next and Future reads ask where; Not for me asks why.
 */
export function BulkBar({
  rows,
  selected,
  setSelected,
  readTypes,
  onPaperState,
}: {
  rows: BulkRow[];
  selected: string[];
  setSelected: (ids: string[]) => void;
  readTypes: string[];
  /** Recently added (check-up, Kristi Nov 2): Mark as ▾ lost or donated for the paper books selected. */
  onPaperState?: (books: BulkRow[], state: 'lost' | 'donated') => void;
}) {
  const notice = useNotice();
  const [place, setPlace] = useState<ListTab | null>(null);
  const [nfm, setNfm] = useState<{ ids: string[]; title: string } | null>(null);
  const inOrder = (list: string[]) => rows.map((r) => r.id).filter((id) => list.includes(id));
  const move = (to: ListTab | 'reference', where?: 'kept' | 'top' | 'bottom') => {
    const ids = inOrder(selected);
    setSelected([]);
    void moveBooks(notice, ids, to, where ? { place: { place: where } } : {}, `moved to ${shelfName(to)}`);
  };
  const set = (field: 'interest' | 'read_type', value: number | string | null, what: string) => void setBooks(notice, inOrder(selected), field, value, what);
  const picked = rows.filter((r) => selected.includes(r.id));
  return (
    <>
      <div class="queue-bulk" role="region" aria-label="Change the selected books">
        <span class="queue-bulk-count">{selectedText(selected.length)}</span>
        {selected.length ? (
          <>
            <BulkMenu
              label="Move"
              note="Start reading and Finished: one book at a time, from its own Move ▾."
              items={[
                { label: 'Up Next…', run: () => setPlace('up_next'), current: shared(picked, (r) => r.status) === 'up_next' },
                { label: 'Future reads…', run: () => setPlace('future_reads'), current: shared(picked, (r) => r.status) === 'future_reads' },
                { label: 'Not for me…', run: () => setNfm({ ids: inOrder(selected), title: selected.length === 1 ? (rows.find((r) => r.id === selected[0])?.title ?? '1 book') : `${num(selected.length)} books` }), current: shared(picked, (r) => r.status) === 'not_for_me' },
                { label: 'Reference', run: () => move('reference'), current: shared(picked, (r) => r.status) === 'reference' },
              ]}
            />
            <BulkMenu label="Read type" items={readTypeItems(readTypes, picked, set)} />
            <BulkMenu label="Interest" items={interestItems(picked, set)} />
            {onPaperState && picked.some((r) => r.formats?.includes('paper')) ? (
              <BulkMenu
                label="Mark as"
                note="The paper copy of each paper book selected."
                items={[
                  { label: 'Paper · lost', run: () => onPaperState(picked.filter((r) => r.formats?.includes('paper')), 'lost') },
                  { label: 'Paper · donated', run: () => onPaperState(picked.filter((r) => r.formats?.includes('paper')), 'donated') },
                ]}
              />
            ) : null}
          </>
        ) : (
          <span class="queue-bulk-hint">Tick books, or select every book that matches.</span>
        )}
        <span class="queue-bulk-end">
          {rows.length > selected.length ? (
            <button type="button" class="btn-link" onClick={() => setSelected(rows.map((r) => r.id))}>
              Select all {num(rows.length)}
            </button>
          ) : null}
          {selected.length ? (
            <button type="button" class="btn-link" onClick={() => setSelected([])}>
              Clear
            </button>
          ) : null}
        </span>
      </div>
      {place ? (
        <BulkPlaceSheet
          to={place}
          count={selected.length}
          onClose={() => setPlace(null)}
          onMove={(where) => {
            const to = place;
            setPlace(null);
            move(to, where);
          }}
        />
      ) : null}
      {nfm ? (
        <NotForMeSheet
          title={nfm.title}
          many={nfm.ids.length > 1}
          onClose={() => setNfm(null)}
          onSave={async (chips, reason) => {
            const d = { chips, reason, where: 'queue' };
            setSelected([]);
            const ok = nfm.ids.length === 1 ? await moveBook(notice, { id: nfm.ids[0]!, title: nfm.title }, 'not_for_me', d, `${nfm.title} moved to Not for me`) : await moveBooks(notice, nfm.ids, 'not_for_me', d, 'moved to Not for me');
            if (ok) setNfm(null);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * A list's top line (its name, Accept all…) pins under the bar; its table's header pins under that
 * line, so this keeps the line's height in --rw-top-h.
 */
export function useTopLine() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement.style;
    const set = () => root.setProperty('--rw-top-h', `${el.offsetHeight}px`);
    set();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      root.removeProperty('--rw-top-h');
    };
  }, []);
  return ref;
}
